/**
 * 🔍 Enhanced Device Fingerprinting Service
 *
 * Gera um fingerprint único e robusto do dispositivo/browser do usuário
 *
 * Técnicas utilizadas:
 * - Canvas Fingerprinting (renderização única por GPU)
 * - WebGL Fingerprinting (detalhes da GPU)
 * - Audio Fingerprinting (processamento de áudio único)
 * - Hardware Info (CPU, memória, concorrência)
 * - Screen & Media (resolução, orientação, pixel ratio)
 * - Timezone & Language
 * - Plugins & MIME types
 * - Fonts Detection
 * - Battery Status
 * - Network Info
 *
 * O fingerprint gerado é um hash SHA-256 de todos os dados coletados
 */

export interface DeviceFingerprint {
  fingerprint: string               // Hash SHA-256 único
  components: FingerprintComponents // Dados brutos coletados
  confidence: number                // 0-100 (quão único é o fingerprint)
  timestamp: number                 // Quando foi gerado
}

export interface FingerprintComponents {
  // Browser & Engine
  userAgent: string
  platform: string
  vendor: string
  language: string
  languages: string[]

  // Screen & Display
  screenResolution: string
  availableScreenResolution: string
  colorDepth: number
  pixelRatio: number

  // Hardware
  hardwareConcurrency: number       // CPU cores
  deviceMemory?: number             // GB de RAM
  maxTouchPoints: number

  // Time & Location
  timezone: string
  timezoneOffset: number

  // Graphics
  canvas: string                    // Canvas fingerprint
  webgl: string | null              // WebGL fingerprint
  webglVendor: string | null
  webglRenderer: string | null

  // Audio
  audio: string | null              // Audio fingerprint

  // Features
  plugins: string[]
  mimeTypes: string[]
  fonts: string[]

  // Storage & Cookies
  cookieEnabled: boolean
  localStorage: boolean
  sessionStorage: boolean
  indexedDB: boolean

  // Network
  doNotTrack: string | null
  connection?: {
    effectiveType?: string
    downlink?: number
    rtt?: number
  }

  // Battery (se disponível)
  battery?: {
    charging: boolean
    level: number
  }
}

/**
 * Gera canvas fingerprint (único por combinação GPU + driver + browser)
 */
function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) return 'canvas_not_supported'

    // Renderizar texto com características únicas
    canvas.width = 200
    canvas.height = 50

    ctx.textBaseline = 'top'
    ctx.font = '14px "Arial"'
    ctx.textBaseline = 'alphabetic'
    ctx.fillStyle = '#f60'
    ctx.fillRect(125, 1, 62, 20)
    ctx.fillStyle = '#069'
    ctx.fillText('Canvas Fingerprint 🔍', 2, 15)
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
    ctx.fillText('Canvas Fingerprint 🔍', 4, 17)

    // Obter data URL da canvas (único por GPU/driver)
    return canvas.toDataURL()
  } catch (error) {
    return 'canvas_error'
  }
}

/**
 * Gera WebGL fingerprint (detalhes da GPU)
 */
function getWebGLFingerprint(): { fingerprint: string | null; vendor: string | null; renderer: string | null } {
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')

    if (!gl) {
      return { fingerprint: null, vendor: null, renderer: null }
    }

    const debugInfo = (gl as any).getExtension('WEBGL_debug_renderer_info')

    let vendor = null
    let renderer = null

    if (debugInfo) {
      vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
      renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
    }

    // Obter parâmetros WebGL únicos
    const params = [
      gl.getParameter(gl.VERSION),
      gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
      gl.getParameter(gl.VENDOR),
      gl.getParameter(gl.RENDERER),
    ].join('~')

    return { fingerprint: params, vendor, renderer }
  } catch (error) {
    return { fingerprint: null, vendor: null, renderer: null }
  }
}

/**
 * Gera audio fingerprint (processamento de áudio único)
 * Usa abordagem simplificada para evitar deprecated ScriptProcessorNode
 */
async function getAudioFingerprint(): Promise<string | null> {
  try {
    // @ts-ignore - AudioContext pode não estar em todos os browsers
    const AudioContext = window.AudioContext || window.webkitAudioContext

    if (!AudioContext) return null

    const context = new AudioContext()
    let isClosed = false // Flag para prevenir double-close

    // Função helper para fechar context de forma segura
    const safeClose = async () => {
      if (!isClosed && context.state !== 'closed') {
        isClosed = true
        try {
          await context.close()
        } catch (error) {
          // Silenciosamente ignorar erro de close
          // AudioContext already closed
        }
      }
    }

    // Usar AnalyserNode em vez de ScriptProcessorNode (não deprecado)
    const oscillator = context.createOscillator()
    const analyser = context.createAnalyser()
    const gainNode = context.createGain()

    gainNode.gain.value = 0 // Silencioso
    oscillator.type = 'triangle'
    oscillator.frequency.value = 10000

    analyser.fftSize = 2048
    const bufferLength = analyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)

    oscillator.connect(analyser)
    analyser.connect(gainNode)
    gainNode.connect(context.destination)

    oscillator.start(0)

    return new Promise((resolve) => {
      // Aguardar um pouco para coletar dados
      setTimeout(async () => {
        try {
          // Obter dados de frequência
          analyser.getByteFrequencyData(dataArray)

          // Criar fingerprint dos primeiros 30 valores
          const fingerprint = Array.from(dataArray.slice(0, 30)).join('')

          // Cleanup
          try {
            oscillator.stop()
          } catch (e) {
            // Já foi parado
          }

          await safeClose()
          resolve(fingerprint)
        } catch (error) {
          await safeClose()
          resolve(null)
        }
      }, 100)

      // Timeout de segurança após 1s
      setTimeout(async () => {
        try {
          oscillator.stop()
        } catch (e) {
          // Já foi parado
        }
        await safeClose()
        if (!isClosed) {
          resolve(null)
        }
      }, 1000)
    })
  } catch (error) {
    return null
  }
}

/**
 * Detecta fontes instaladas (único por sistema)
 */
function getInstalledFonts(): string[] {
  const baseFonts = ['monospace', 'sans-serif', 'serif']
  const testFonts = [
    'Arial', 'Verdana', 'Times New Roman', 'Courier New', 'Georgia',
    'Palatino', 'Garamond', 'Bookman', 'Comic Sans MS', 'Trebuchet MS',
    'Impact', 'Lucida Console', 'Tahoma', 'Geneva', 'Helvetica',
  ]

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return []

  const testString = 'mmmmmmmmmmlli'
  const testSize = '72px'

  // Obter largura baseline para cada fonte base
  const baselines: Record<string, number> = {}
  baseFonts.forEach(baseFont => {
    ctx.font = `${testSize} ${baseFont}`
    baselines[baseFont] = ctx.measureText(testString).width
  })

  // Testar cada fonte
  const installedFonts: string[] = []

  testFonts.forEach(testFont => {
    let detected = false

    baseFonts.forEach(baseFont => {
      ctx.font = `${testSize} '${testFont}', ${baseFont}`
      const width = ctx.measureText(testString).width

      if (width !== baselines[baseFont]) {
        detected = true
      }
    })

    if (detected) {
      installedFonts.push(testFont)
    }
  })

  return installedFonts
}

/**
 * Obter informações de bateria (se disponível)
 */
async function getBatteryInfo(): Promise<{ charging: boolean; level: number } | undefined> {
  try {
    // @ts-ignore
    if (!navigator.getBattery) return undefined

    // @ts-ignore
    const battery = await navigator.getBattery()
    return {
      charging: battery.charging,
      level: Math.round(battery.level * 100),
    }
  } catch (error) {
    return undefined
  }
}

/**
 * Gerar hash SHA-256 (simulado via SubtleCrypto ou fallback)
 */
async function generateHash(data: string): Promise<string> {
  try {
    const encoder = new TextEncoder()
    const dataBuffer = encoder.encode(data)
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    return hashHex
  } catch (error) {
    // Fallback: hash simples (não criptográfico, mas suficiente)
    let hash = 0
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16)
  }
}

/**
 * Calcular confidence score do fingerprint (0-100)
 * Baseado em quantos componentes únicos foram coletados
 */
function calculateConfidence(components: FingerprintComponents): number {
  let score = 0
  let maxScore = 0

  // Canvas (25 pontos)
  maxScore += 25
  if (components.canvas && components.canvas !== 'canvas_not_supported') score += 25

  // WebGL (20 pontos)
  maxScore += 20
  if (components.webgl && components.webglVendor) score += 20

  // Audio (15 pontos)
  maxScore += 15
  if (components.audio) score += 15

  // Fonts (10 pontos)
  maxScore += 10
  if (components.fonts.length > 5) score += 10

  // Hardware (10 pontos)
  maxScore += 10
  if (components.hardwareConcurrency > 1 && components.deviceMemory) score += 10

  // Screen (5 pontos)
  maxScore += 5
  if (components.screenResolution && components.colorDepth) score += 5

  // Plugins (5 pontos)
  maxScore += 5
  if (components.plugins.length > 0) score += 5

  // Storage (5 pontos)
  maxScore += 5
  if (components.localStorage && components.indexedDB) score += 5

  // Battery (5 pontos)
  maxScore += 5
  if (components.battery) score += 5

  return Math.round((score / maxScore) * 100)
}

/**
 * Gerar fingerprint completo do dispositivo
 */
export async function generateDeviceFingerprint(): Promise<DeviceFingerprint> {
  // Coletar todos os componentes
  const canvasFingerprint = getCanvasFingerprint()
  const webglData = getWebGLFingerprint()
  const audioFingerprint = await getAudioFingerprint()
  const installedFonts = getInstalledFonts()
  const batteryInfo = await getBatteryInfo()

  // @ts-ignore - Algumas propriedades podem não existir em todos os browsers
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection

  const components: FingerprintComponents = {
    // Browser
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    vendor: navigator.vendor,
    language: navigator.language,
    languages: navigator.languages ? Array.from(navigator.languages) : [navigator.language],

    // Screen
    screenResolution: `${screen.width}x${screen.height}`,
    availableScreenResolution: `${screen.availWidth}x${screen.availHeight}`,
    colorDepth: screen.colorDepth,
    pixelRatio: window.devicePixelRatio,

    // Hardware
    hardwareConcurrency: navigator.hardwareConcurrency || 1,
    // @ts-ignore
    deviceMemory: navigator.deviceMemory,
    maxTouchPoints: navigator.maxTouchPoints || 0,

    // Time
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),

    // Graphics
    canvas: canvasFingerprint,
    webgl: webglData.fingerprint,
    webglVendor: webglData.vendor,
    webglRenderer: webglData.renderer,

    // Audio
    audio: audioFingerprint,

    // Features
    plugins: Array.from(navigator.plugins).map(p => p.name),
    mimeTypes: Array.from(navigator.mimeTypes).map(m => m.type),
    fonts: installedFonts,

    // Storage
    cookieEnabled: navigator.cookieEnabled,
    localStorage: !!window.localStorage,
    sessionStorage: !!window.sessionStorage,
    indexedDB: !!window.indexedDB,

    // Network
    doNotTrack: navigator.doNotTrack,
    connection: connection ? {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
    } : undefined,

    // Battery
    battery: batteryInfo,
  }

  // Gerar string com todos os componentes
  const fingerprintData = JSON.stringify(components)

  // Gerar hash SHA-256
  const fingerprint = await generateHash(fingerprintData)

  // Calcular confidence
  const confidence = calculateConfidence(components)

  return {
    fingerprint,
    components,
    confidence,
    timestamp: Date.now(),
  }
}

/**
 * Comparar dois fingerprints (similaridade 0-100)
 */
export function compareFingerprints(fp1: DeviceFingerprint, fp2: DeviceFingerprint): number {
  if (fp1.fingerprint === fp2.fingerprint) return 100

  let matches = 0
  let total = 0

  const c1 = fp1.components
  const c2 = fp2.components

  // Comparar componentes principais
  if (c1.userAgent === c2.userAgent) matches++
  total++

  if (c1.screenResolution === c2.screenResolution) matches++
  total++

  if (c1.timezone === c2.timezone) matches++
  total++

  if (c1.canvas === c2.canvas) matches += 3 // Canvas é muito importante
  total += 3

  if (c1.webgl === c2.webgl) matches += 2
  total += 2

  if (c1.audio === c2.audio && c1.audio !== null) matches += 2
  total += 2

  return Math.round((matches / total) * 100)
}
